import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchProgressNotePrp } from "../../../store/slices/progressNotePrpSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function ProgressNotePrpPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentProgressNotePrp, isLoading, error } = useSelector(
    (state) => state.progressNotePrp
  );

  useEffect(() => {
    if (id) dispatch(fetchProgressNotePrp(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentProgressNotePrp) {
      const t = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(t);
        window.onafterprint = null;
      };
    }
  }, [currentProgressNotePrp, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading Progress Notes PRP print...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentProgressNotePrp) return null;

  const d = currentProgressNotePrp;

  return (
    <>
      <style jsx global>{`
        body { margin: 0 !important; padding: 0 !important; }
        ${printFooterStyles}
        @media print {
          @page {
            size: A4;
            margin: 20mm 15mm 20mm 15mm;
          }
          body {
            background: #fff !important;
            color: #000 !important;
          }
          .no-print {
            display: none !important;
          }
        }

        * {
          box-sizing: border-box !important;
          font-family: "Times New Roman", Times, serif !important;
        }

        body {
          background: #fff;
          color: #000;
          font-size: 11pt !important;
          line-height: 1.4 !important;
        }

        .print-container {
          padding: 20px !important;
          width: 100% !important;
          max-width: 800px;
          margin: 0 auto;
        }

        .header-logo {
          display: flex;
          justify-content: center;
          margin-bottom: 10px;
        }

        .header-logo img {
          height: 70px;
        }

        .header-text {
          text-align: center;
          margin-bottom: 25px;
        }

        .header-text h2 {
          font-size: 18pt !important;
          font-weight: bold !important;
          margin: 0 0 4px 0 !important;
          text-transform: uppercase;
        }

        .header-text h3 {
          font-size: 13pt !important;
          font-weight: bold !important;
          margin: 0 !important;
          text-transform: uppercase;
          border-bottom: 2px solid #000;
          display: inline-block;
          padding-bottom: 3px;
        }

        .client-info-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 30px;
        }

        .client-info-table td {
          padding: 8px 12px;
          border: 1px solid #000;
          font-size: 11pt;
        }

        .lbl-bold {
          font-weight: bold;
          background-color: #f5f5f5;
          width: 20%;
        }

        .val-text {
          width: 30%;
        }

        .session-card {
          border: 1px solid #000;
          padding: 15px;
          margin-bottom: 25px;
          page-break-inside: avoid;
        }

        .session-header {
          font-size: 12pt !important;
          font-weight: bold !important;
          border-bottom: 1px solid #000;
          padding-bottom: 5px;
          margin-bottom: 12px;
          text-transform: uppercase;
          display: flex;
          justify-content: space-between;
        }

        .detail-row {
          margin-bottom: 8px;
          display: flex;
          align-items: flex-start;
        }

        .detail-label {
          font-weight: bold;
          min-width: 160px;
          font-size: 10.5pt;
        }

        .detail-value {
          flex-grow: 1;
          font-size: 10.5pt;
          white-space: pre-wrap;
        }

        .footer-signatures {
          margin-top: 50px;
          display: flex;
          justify-content: space-between;
          page-break-inside: avoid;
        }

        .signature-line {
          width: 220px;
          border-top: 1px solid #000;
          text-align: center;
          padding-top: 5px;
          font-size: 10pt;
        }
      `}</style>

      <PrintWrapper>
        <div className="print-container">
        {/* LOGO */}
        <div className="header-logo">
          <img src={logo} alt="The Recovery House Logo" />
        </div>

        {/* HEADER */}
        <div className="header-text">
          <h2>THE RECOVERY HOUSE (TRH)</h2>
          <h3>Progress Note — PRP</h3>
        </div>

        {/* CLIENT DETAILS TABLE */}
        <table className="client-info-table">
          <tbody>
            <tr>
              <td className="lbl-bold">Client Name</td>
              <td className="val-text">
                {d.patient ? `${d.patient.first_name || ""} ${d.patient.last_name || ""}`.trim() : "-"}
              </td>
              <td className="lbl-bold">MR Number</td>
              <td className="val-text">{d.patient?.mr_no || "-"}</td>
            </tr>
            <tr>
              <td className="lbl-bold">Date of Birth</td>
              <td className="val-text">
                {d.patient?.dob ? new Date(d.patient.dob).toLocaleDateString("en-GB") : "-"}
              </td>
              <td className="lbl-bold">Case Number</td>
              <td className="val-text">{d.case?.case_number || "-"}</td>
            </tr>
          </tbody>
        </table>

        {/* SESSION REPEATS */}
        {d.details && d.details.map((row, idx) => (
          <div className="session-card" key={row.id || idx}>
            <div className="session-header">
              <span>Session {idx + 1}</span>
              <span style={{ fontSize: '10.5pt', fontWeight: 'normal' }}>
                Date: {row.session_date ? new Date(row.session_date).toLocaleString("en-GB") : "-"}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Goal Achievement (%):</span>
              <span className="detail-value">{row.goal_achievement || "-"}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Symptoms:</span>
              <span className="detail-value">{row.symptoms || "-"}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Functional Progress:</span>
              <span className="detail-value">{row.functional_progress || "-"}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Participation:</span>
              <span className="detail-value">{row.participation || "-"}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Barriers:</span>
              <span className="detail-value">{row.barriers || "-"}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Adjustments / Plan:</span>
              <span className="detail-value">{row.adjustments_plan || "-"}</span>
            </div>
          </div>
        ))}

        {/* SIGNATURES */}
        <div className="footer-signatures">
          <div className="signature-line">PRP Instructor / Specialist</div>
          <div className="signature-line">Program Manager Signature</div>
        </div>
      </div>
      </PrintWrapper>
    </>
  );
}
